/**
 * Thin fetch wrapper for the ERM API.
 *
 * Deliberately dependency-free (no axios): the whole client is one file, and
 * adding an HTTP library would be the first of many stack decisions this app
 * has not made yet.
 */

// Same convention as Cloudmyle-UI: an explicit REACT_APP_API_BASE wins; otherwise
// production builds talk to the live API and local development to localhost.
// A deployed build must never fall back to localhost - in a user's browser that
// means their own computer, not our server.
export const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === 'production' ? 'https://api.cloudmyle.com' : 'http://localhost:8081');
const BASE = API_BASE;
const TOKEN_KEY = 'erm_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

/** Thrown for any non-2xx response, carrying the backend's own message. */
export class ApiError extends Error {
  constructor(status, message) {
    super(message);
    this.status = status;
  }
}

async function request(path, { method = 'GET', body, auth = true } = {}) {
  const headers = {};
  // FormData sets its own multipart boundary header; everything else is JSON.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';

  const token = getToken();
  if (auth && token) headers.Authorization = `Bearer ${token}`;

  let res;
  try {
    res = await fetch(BASE + path, {
      method,
      headers,
      body: body === undefined ? undefined : (isForm ? body : JSON.stringify(body)),
    });
  } catch (e) {
    // fetch only rejects on network-level failure, so this is almost always
    // "backend isn't running" rather than a bad request.
    throw new ApiError(0, `Cannot reach the API at ${BASE}. Is the backend running?`);
  }

  if (res.status === 204) return null;

  const text = await res.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    if (res.status === 403 && payload && payload.code === 'PASSWORD_CHANGE_REQUIRED') {
      // Let the app switch to the forced-change screen wherever this happens.
      window.dispatchEvent(new Event('erm:password-change-required'));
    }
    // Spring sets server.error.include-message=always, so validation failures
    // arrive as {message: "..."} - surface that rather than a bare status.
    // Spring fills "message" with the useless "No message available" when a
    // route does not exist - fall back to the status so a 404 reads as one.
    const serverMsg = payload && payload.message && payload.message !== 'No message available'
      ? payload.message : null;
    const msg =
      serverMsg ||
      (typeof payload === 'string' && payload) ||
      (res.status === 404
        ? `Not found (404): ${path} - this server may be running an older build.`
        : `Request failed (${res.status}${payload && payload.error ? ` ${payload.error}` : ''})`);
    throw new ApiError(res.status, msg);
  }

  return payload;
}

export const api = {
  get: (p) => request(p),
  post: (p, body) => request(p, { method: 'POST', body }),
  put: (p, body) => request(p, { method: 'PUT', body }),
  patch: (p, body) => request(p, { method: 'PATCH', body }),
  del: (p) => request(p, { method: 'DELETE' }),
  /** Multipart upload: pass a FormData (e.g. form.append('file', file)). */
  upload: (p, formData) => request(p, { method: 'POST', body: formData }),
};

// --- auth -------------------------------------------------------------

export async function login(email, password) {
  const res = await request('/auth/login', {
    method: 'POST',
    body: { email, password },
    auth: false,
  });
  if (!res || !res.authToken) throw new ApiError(500, 'Login succeeded but returned no token');
  setToken(res.authToken);
  return res;
}

// --- erm endpoints ----------------------------------------------------

export const employees = {
  list: (includeExited = false) => api.get(`/api/erm/employees?includeExited=${includeExited}`),
  get: (id) => api.get(`/api/erm/employees/${id}`),
  create: (b) => api.post('/api/erm/employees', b),
  nextCode: () => api.get('/api/erm/employees/next-code'),
  update: (id, b) => api.put(`/api/erm/employees/${id}`, b),
  team: (id, includeExited = false) =>
    api.get(`/api/erm/employees/${id}/team?includeExited=${includeExited}`),
  chain: (id) => api.get(`/api/erm/employees/${id}/management-chain`),
  headcount: () => api.get('/api/erm/employees/headcount'),
  exit: (id, date) =>
    api.post(`/api/erm/employees/${id}/exit${date ? `?exitDate=${date}` : ''}`),
};

export const departments = {
  list: (activeOnly = false) => api.get(`/api/erm/departments?activeOnly=${activeOnly}`),
  create: (b) => api.post('/api/erm/departments', b),
  update: (id, b) => api.put(`/api/erm/departments/${id}`, b),
  deactivate: (id) => api.post(`/api/erm/departments/${id}/deactivate`),
  del: (id) => api.del(`/api/erm/departments/${id}`),
};

export const designations = {
  list: (activeOnly = false) => api.get(`/api/erm/designations?activeOnly=${activeOnly}`),
  create: (b) => api.post('/api/erm/designations', b),
  update: (id, b) => api.put(`/api/erm/designations/${id}`, b),
  deactivate: (id) => api.post(`/api/erm/designations/${id}/deactivate`),
};

export const gitMetrics = {
  status: () => api.get('/api/erm/git/status'),
  connection: () => api.get('/api/erm/git/connection'),

  repos: () => api.get('/api/erm/git/repos'),
  discover: () => api.post('/api/erm/git/repos/discover'),
  setTracked: (id, tracked) =>
    api.post(`/api/erm/git/repos/${id}/tracked?tracked=${tracked}`),

  collect: () => api.post('/api/erm/git/collect'),
  collectProgress: () => api.get('/api/erm/git/collect/progress'),
  trackAll: () => api.post('/api/erm/git/repos/track-all'),
  score: () => api.post('/api/erm/git/score'),
  scoreProgress: () => api.get('/api/erm/git/score/progress'),
  stopScoring: () => api.post('/api/erm/git/score/stop'),

  leaderboard: (from, to) =>
    api.get(`/api/erm/git/leaderboard?from=${from}&to=${to}`),
  developer: (id, from, to) =>
    api.get(`/api/erm/git/developer/${id}?from=${from}&to=${to}`),
  timeline: (id, from, to) =>
    api.get(`/api/erm/git/developer/${id}/timeline?from=${from}&to=${to}`),
};

export const gitIdentities = {
  list: () => api.get('/api/erm/git/identities'),
  unmapped: () => api.get('/api/erm/git/identities/unmapped'),
  create: (employeeId, value, kind = 'AUTHOR_EMAIL') =>
    api.post('/api/erm/git/identities', { employeeId, value, kind }),
  del: (id) => api.del(`/api/erm/git/identities/${id}`),
};

// --- employee portal --------------------------------------------------

export const account = {
  me: () => api.get('/api/erm/accounts/me'),
  // Own record plus manager's name - employees.get cannot read the manager.
  profile: () => api.get('/api/erm/accounts/me/profile'),
  list: () => api.get('/api/erm/accounts'),
  // role: USER or ADMIN. Manager rights come from the reporting line, not the role.
  createLogin: (employeeId, role = 'USER') =>
    api.post(`/api/erm/accounts/${employeeId}/create-login?role=${role}`),
  linkExisting: (employeeId) => api.post(`/api/erm/accounts/${employeeId}/link-existing`),
  resetPassword: (employeeId) => api.post(`/api/erm/accounts/${employeeId}/reset-password`),
  changePassword: (currentPassword, newPassword) =>
    api.post('/api/erm/accounts/me/password', { currentPassword, newPassword }),
  unlink: (employeeId) => api.post(`/api/erm/accounts/${employeeId}/unlink`),
};

export const projects = {
  list: (activeOnly = false) => api.get(`/api/erm/projects?activeOnly=${activeOnly}`),
  bookable: (employeeId) => api.get(`/api/erm/projects/bookable/${employeeId}`),
  nextCode: (prefix) =>
    api.get(`/api/erm/projects/next-code${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ''}`),
  create: (b) => api.post('/api/erm/projects', b),
  update: (id, b) => api.put(`/api/erm/projects/${id}`, b),
  close: (id) => api.post(`/api/erm/projects/${id}/close`),
};

export const time = {
  today: () => api.get('/api/erm/time/attendance/today'),
  punchIn: (mode, note) =>
    api.post(`/api/erm/time/attendance/punch-in?mode=${mode}${note ? `&note=${encodeURIComponent(note)}` : ''}`),
  setMode: (mode) => api.post(`/api/erm/time/attendance/mode?mode=${mode}`),
  summary: (from, to) => api.get(`/api/erm/time/attendance/summary?from=${from}&to=${to}`),
  punchOut: (breakMinutes) =>
    api.post(`/api/erm/time/attendance/punch-out${breakMinutes ? `?breakMinutes=${breakMinutes}` : ''}`),
  attendance: (from, to) => api.get(`/api/erm/time/attendance?from=${from}&to=${to}`),

  week: (date) => api.get(`/api/erm/time/timesheet/week${date ? `?date=${date}` : ''}`),
  saveWeek: (date, entries, employeeNote) =>
    api.post('/api/erm/time/timesheet/week', { date, entries, employeeNote }),
  submitWeek: (date) => api.post(`/api/erm/time/timesheet/submit?date=${date}`),
  pending: () => api.get('/api/erm/time/timesheet/pending'),
  decide: (id, approve, note) =>
    api.post(`/api/erm/time/timesheet/${id}/decide?approve=${approve}${note ? `&note=${encodeURIComponent(note)}` : ''}`),
};

export const leave = {
  types: () => api.get('/api/erm/leave/types'),
  createType: (b) => api.post('/api/erm/leave/types', b),
  holidays: () => api.get('/api/erm/leave/holidays'),
  createHoliday: (b) => api.post('/api/erm/leave/holidays', b),
  workingDays: (from, to) => api.get(`/api/erm/leave/working-days?from=${from}&to=${to}`),

  balances: (employeeId, year) => {
    const q = [];
    if (employeeId) q.push(`employeeId=${employeeId}`);
    if (year) q.push(`year=${year}`);
    return api.get(`/api/erm/leave/balances${q.length ? `?${q.join('&')}` : ''}`);
  },
  requests: (employeeId) =>
    api.get(`/api/erm/leave/requests${employeeId ? `?employeeId=${employeeId}` : ''}`),
  apply: (b) => api.post('/api/erm/leave/requests', b),
  cancel: (id) => api.post(`/api/erm/leave/requests/${id}/cancel`),

  pending: () => api.get('/api/erm/leave/pending'),
  decide: (id, approve, note) =>
    api.post(`/api/erm/leave/requests/${id}/decide?approve=${approve}${note ? `&note=${encodeURIComponent(note)}` : ''}`),
  teamCalendar: (from, to) => api.get(`/api/erm/leave/team-calendar?from=${from}&to=${to}`),

  runAccrual: (month) => api.post(`/api/erm/leave/accrual/run${month ? `?month=${month}` : ''}`),
  carryForward: (year) => api.post(`/api/erm/leave/accrual/carry-forward?year=${year}`),

  // HR/Admin only
  adminBalances: (year) => api.get(`/api/erm/leave/admin/balances${year ? `?year=${year}` : ''}`),
  setBalance: (b) => api.post('/api/erm/leave/admin/balances', b),
  adjustments: (employeeId) =>
    api.get(`/api/erm/leave/admin/adjustments${employeeId ? `?employeeId=${employeeId}` : ''}`),
};

// --- work: boards, tickets, sprints -------------------------------------
export const work = {
  projects: () => api.get('/api/erm/work/projects'),
  setKey: (projectId, key) => api.put(`/api/erm/work/projects/${projectId}/key`, { key }),
  people: () => api.get('/api/erm/work/people'),
  board: (projectId, mode) => api.get(`/api/erm/work/projects/${projectId}/board?mode=${mode}`),
  backlog: (projectId) => api.get(`/api/erm/work/projects/${projectId}/backlog`),
  create: (projectId, body) => api.post(`/api/erm/work/projects/${projectId}/items`, body),
  item: (id) => api.get(`/api/erm/work/items/${id}`),
  update: (id, patch) => api.patch(`/api/erm/work/items/${id}`, patch),
  move: (id, body) => api.post(`/api/erm/work/items/${id}/move`, body),
  remove: (id) => api.del(`/api/erm/work/items/${id}`),
  byKey: (key) => api.get(`/api/erm/work/items/by-key/${encodeURIComponent(key)}`),
  archive: (id) => api.post(`/api/erm/work/items/${id}/archive`),
  restore: (id) => api.post(`/api/erm/work/items/${id}/restore`),
  archived: (projectId) => api.get(`/api/erm/work/projects/${projectId}/archived`),
  // custom workflow (board columns)
  columns: (projectId) => api.get(`/api/erm/work/projects/${projectId}/columns`),
  customiseColumns: (projectId) => api.post(`/api/erm/work/projects/${projectId}/columns/customise`),
  createColumn: (projectId, body) => api.post(`/api/erm/work/projects/${projectId}/columns`, body),
  updateColumn: (id, body) => api.patch(`/api/erm/work/columns/${id}`, body),
  reorderColumns: (projectId, ids) => api.post(`/api/erm/work/projects/${projectId}/columns/reorder`, { ids }),
  deleteColumn: (id, moveTo) => api.del(`/api/erm/work/columns/${id}${moveTo ? `?moveTo=${moveTo}` : ''}`),
  resetColumns: (projectId) => api.del(`/api/erm/work/projects/${projectId}/columns`),
  // toClient: true shares the comment with invited client people; team comments are internal by default.
  comment: (id, body, toClient = false) => api.post(`/api/erm/work/items/${id}/comments`, { body, toClient }),
  editComment: (id, body) => api.put(`/api/erm/work/comments/${id}`, { body }),
  deleteComment: (id) => api.del(`/api/erm/work/comments/${id}`),
  createSprint: (projectId, body) => api.post(`/api/erm/work/projects/${projectId}/sprints`, body),
  updateSprint: (id, body) => api.patch(`/api/erm/work/sprints/${id}`, body),
  startSprint: (id, body) => api.post(`/api/erm/work/sprints/${id}/start`, body),
  completeSprint: (id, moveTo) => api.post(`/api/erm/work/sprints/${id}/complete`, { moveTo }),
  deleteSprint: (id) => api.del(`/api/erm/work/sprints/${id}`),
  briefStatus: () => api.get('/api/erm/work/brief/status'),
  /** body: {brief, provider, granularity: 'compact'|'detailed', documents: [{name, text}]} */
  draftBrief: (projectId, body) => api.post(`/api/erm/work/projects/${projectId}/brief`, body),
  briefDocument: (projectId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.upload(`/api/erm/work/projects/${projectId}/brief/documents`, form);
  },
  briefJob: (jobId) => api.get(`/api/erm/work/brief/jobs/${jobId}`),
  applyBrief: (projectId, body) => api.post(`/api/erm/work/projects/${projectId}/brief/apply`, body),
};

// --- project costing (money fields are returned to HR/Admin only) ---------
export const costing = {
  rates: () => api.get('/api/erm/costing/rates'),
  history: (employeeId) => api.get(`/api/erm/costing/rates/${employeeId}`),
  addRate: (b) => api.post('/api/erm/costing/rates', b),
  deleteRate: (id) => api.del(`/api/erm/costing/rates/${id}`),
  projects: (from, to) => api.get(`/api/erm/costing/projects?from=${from}&to=${to}`),
  project: (id, from, to) => api.get(`/api/erm/costing/projects/${id}?from=${from}&to=${to}`),
  utilisation: (from, to) => api.get(`/api/erm/costing/utilisation?from=${from}&to=${to}`),
};
